import { clamp } from 'src/helpers/MathUtils'
import EventEmitter from 'src/helpers/EventEmitter'
import EventObserver from 'src/helpers/EventObserver'
import device from 'src/libs/device'
import env from 'src/config/env'

export const REM_SCALE_MODES = {
  FIT: 'fit',
}

export const REM_SCALE_UNITS = {
  WINDOW: 'WINDOW',
  PERCENT: 'PERCENT',
  VIEWPORT: 'VIEWPORT',
}

export const EVENTS = {
  RESIZE: 'resize',
}

export const DEVICE_MOBILE = 'mobile'
export const DEVICE_TABLET = 'tablet'
export const DEVICE_DESKTOP = 'desktop'

export const BREAKPOINT_MOBILE = 'breakpointMobile'
export const BREAKPOINT_MOBILE_LANDSCAPE = 'breakpointMobileLandscape'
export const BREAKPOINT_TABLET = 'breakpointTablet'
export const BREAKPOINT_DESKTOP = 'breakpointDesktop'

let defaultConfig = {
  alwaysEmitResize: env.ios && env.facebook_video_ad ? true : false, // always trigger resize event even if dimensions haven't changed
  remScaleMode: REM_SCALE_MODES.FIT,
  remScaleUnit: REM_SCALE_UNITS.WINDOW,
  scale: 1,
  baseFontSize: 10,

  breakpoints: {
    [BREAKPOINT_MOBILE]: {
      name: BREAKPOINT_MOBILE,
      maxWidth: 550,
    },

    [BREAKPOINT_TABLET]: {
      name: BREAKPOINT_TABLET,
      maxWidth: 1024,
    },

    [BREAKPOINT_DESKTOP]: {
      name: BREAKPOINT_DESKTOP,
      maxWidth: 20000,
    },
  },

  deviceTypes: {
    [DEVICE_MOBILE]: {
      portrait: {
        designSize: {
          width: 375,
          height: 667,
        },
        remScale: {
          min: 0.9,
          max: 1,
        },
      },

      landscape: {
        designSize: {
          width: 667,
          height: 375,
        },
        remScale: {
          min: 0.85,
          max: 1.1,
        },
      },
    },

    [DEVICE_TABLET]: {
      designSize: {
        width: 1024,
        height: 768,
      },
      remScale: {
        min: 0.7,
        max: 0.9,
      },
    },

    [DEVICE_DESKTOP]: {
      designSize: {
        width: 1366,
        height: 768,
      },
      remScale: {
        min: 1.0,
        max: 1.2,
      },
    },
  },
}

class ResizeService {
  static get EVENTS() {
    return EVENTS
  }
  get EVENTS() {
    return EVENTS
  }

  constructor(config) {
    Object.assign(this, EventEmitter)
    Object.assign(this, EventObserver)

    this.config = Object.assign({}, defaultConfig, config)

    this._makeRulers()

    this.html = document.querySelector('html')

    this.env = env
    //add env broswer classes to html. note: device.js adds desktop, mobile etc
    for (let n in this.env) {
      if (this.env[n]) {
        this.html.classList.add(n)
      }
    }

    this._scale = 1
    this._resize()

    window.addEventListener('resize', () => {
      setTimeout(this._resize, 100)
    })

    //this.onFrame()
  }

  destroy() {
    this.unlisten()
  }

  get wh() {
    return window.innerHeight
  }
  get ww() {
    return window.innerWidth
  }

  get vh() {
    return this.el_vh.clientHeight /*css 100vh*/
  }
  get vw() {
    return window.innerWidth /*css 100vw*/
  }

  get perc_h() {
    return this.el_perc.clientHeight /*css 100%*/
  }
  get perc_w() {
    return window.innerWidth /*css 100%*/
  }

  get isMobile() {
    return this.deviceType === DEVICE_MOBILE
  }
  get isTablet() {
    return this.deviceType === DEVICE_TABLET
  }
  get isDesktop() {
    return this.deviceType === DEVICE_DESKTOP
  }

  get scale() {
    return this._scale
  }
  set scale(val) {
    this._scale = val
    this.fontSize = this.config.baseFontSize * this._scale
    this.html.style.fontSize = `${this.fontSize}px`
  }

  get breakpoint() {
    let bps = this.config.breakpoints
    for (let b in bps) {
      if (this.width <= bps[b].maxWidth) {
        return b
      }
    }
    return BREAKPOINT_DESKTOP
  }

  get landscape() {
    return this.ww > this.wh
  }

  get orientation() {
    return this.ww < this.wh ? 'portrait' : 'landscape'
  }

  //deprecated
  get device() {
    return this.deviceType
  }

  get deviceType() {
    let dt = DEVICE_DESKTOP
    if (device.tablet()) {
      dt = DEVICE_TABLET
    }
    if (device.mobile()) {
      dt = DEVICE_MOBILE
    }
    return dt
  }

  //reconfigure

  get breakpoints() {
    return this.config.breakpoints
  }
  set breakpoints(config) {
    this.config.breakpoints = config
    this._resize()
  }

  get devices() {
    return this.config.devices
  }
  set devices(config) {
    this.config.devices = config
    this._resize()
  }

  get baseFontSize() {
    return this.config.baseFontSize
  }
  set baseFontSize(val) {
    this.config.baseFontSize = val
    this._resize()
  }

  onFrame = () => {
    this._resize()
    //window.requestAnimationFrame(this.onFrame)
  }

  ///INTERNAL UTIL:

  _resize = (forceEmit) => {
    let w = this.ww
    let h = this.wh

    let changed = w !== this.width || h !== this.height
    let trigger = changed || this.config.alwaysEmitResize || forceEmit

    this.width = w
    this.height = h

    if (changed) {
      this._updateFontSize()
      this._updateBreakpointClass()
    }

    if (trigger) {
      this.emit(EVENTS.RESIZE, {
        width: this.width,
        height: this.height,
        breakpoint: this.breakpoint,
        deviceType: this.deviceType,
        orientation: this.orientation,
      })
    }
  }

  _getRemScaleHeight = () => {
    if (this.config.remScaleUnit === REM_SCALE_UNITS.VIEWPORT) {
      return this.vh
    }
    if (this.config.remScaleUnit === REM_SCALE_UNITS.PERCENT) {
      return this.perc_h
    }
    return this.wh
  }

  _getRemScaleWidth = () => {
    if (this.config.remScaleUnit === REM_SCALE_UNITS.VIEWPORT) {
      return this.vw
    }
    if (this.config.remScaleUnit === REM_SCALE_UNITS.PERCENT) {
      return this.perc_w
    }
    return this.ww
  }

  _updateFontSize = () => {
    let ww = this._getRemScaleWidth()
    let wh = this._getRemScaleHeight()

    let d = this.config.deviceTypes[this.deviceType]
    if (d[this.orientation]) {
      d = d[this.orientation]
    } // can have separate portrait/landscape logic
    let scaleX = ww / d.designSize.width
    let scaleY = wh / d.designSize.height
 
    //debugger 
    if(this.deviceType === DEVICE_MOBILE){
      this.scale = clamp(scaleY,  d.remScale.min, d.remScale.max);
    } else {
      //this.scale = clamp(Math.min(scaleX, scaleY),  d.remScale.min, d.remScale.max);
      this.scale = clamp(Math.max(scaleX, scaleY), d.remScale.min, d.remScale.max)
    }
  }

  _updateBreakpointClass = () => {
    let breakpoint = this.breakpoint
    let bps = this.config.breakpoints
    for (let b in bps) {
      this.html.classList[b === breakpoint ? 'add' : 'remove'](b)
    }
  }

  _makeRulers() {
    this.el_vh = document.createElement('div')
    this.el_vh.style.cssText = `position: absolute;
                              pointer-events:none;
                              top:0px;
                              width:1px;
                              height:100vh;
                              `
    this.el_perc = document.createElement('div')
    this.el_perc.style.cssText = `position: absolute;
                                pointer-events:none;
                                top:0px;
                                width:1px;
                                height:100%;
                                `

    document.body.appendChild(this.el_vh)
    document.body.appendChild(this.el_perc)
  }
}

let service = new ResizeService()
service.Events = EVENTS
window.ResizeService = service
export default service
